import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { db } from '../../db/database';
import { useUI } from '../../context/UIContext';
import { useLiveQuery } from 'dexie-react-hooks';
import { pushWorkspaceBackup } from '../../services/cloudBackup';
import * as authService from '../../services/authService';

export default function Sidebar({ mobileOpen = false, onMobileClose, onOpenProposalSearch }) {
  const [collapsed, setCollapsed] = useState(false);
  const { showToast } = useUI();

  // Sync State
  const [syncStatus, setSyncStatus] = useState('Idle');
  const [isConfigured, setIsConfigured] = useState(false);
  const [countdownDisplay, setCountdownDisplay] = useState(null);

  // Stable timer refs
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const syncFnRef = useRef(null);

  // Live settings query
  const liveSettings = useLiveQuery(() => db.settings.get('global'));
  const location = useLocation();

  // Auth session check
  const hasSession = !!authService.getActiveSessionToken();

  // Navigation items
  const navItems = [
    { path: '/', icon: FiIcons.FiPieChart, label: 'Dashboard' },
    { path: '/proposals', icon: FiIcons.FiFileText, label: 'Proposals' },
    { path: '/library', icon: FiIcons.FiBookOpen, label: 'Snippet Library' },
    { path: '/settings', icon: FiIcons.FiSettings, label: 'Settings' },
  ];

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen) onMobileClose?.();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile drawer on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && mobileOpen) onMobileClose?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mobileOpen, onMobileClose]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Sync function stored in ref
  syncFnRef.current = async () => {
    const currentSettings = liveSettings;
    if (!currentSettings?.googleBackupUrl || !currentSettings?.googleBackupConnected) {
      setSyncStatus('Idle');
      return;
    }

    // Keep local work available when the browser is offline. The next normal
    // auto-sync (or the online event below) will retry without treating this as
    // a destructive error.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setSyncStatus('Pending');
      if (currentSettings?.googleAutoSyncEnabled) {
        countdownRef.current = currentSettings.googleSyncInterval * 60;
        setCountdownDisplay(countdownRef.current);
      }
      return;
    }

    setSyncStatus('Syncing');
    try {
      const result = await pushWorkspaceBackup(currentSettings);
      if (result.success) {
        const isoTimestamp = new Date().toISOString();
        await db.settings.update('global', { googleBackupLastSyncAt: isoTimestamp });
        setSyncStatus('Synced');
      } else {
        setSyncStatus('Pending');
      }
    } catch (err) {
      console.error('Auto-sync deferred:', err);
      setSyncStatus('Pending');
    }
    if (currentSettings?.googleAutoSyncEnabled) {
      countdownRef.current = currentSettings.googleSyncInterval * 60;
      setCountdownDisplay(countdownRef.current);
    }
  };

  // Stable auto-sync timer
  useEffect(() => {
    if (!liveSettings) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    const isReady = liveSettings.googleBackupConnected && liveSettings.googleBackupUrl;
    setIsConfigured(isReady);
    if (isReady && liveSettings.googleAutoSyncEnabled) {
      countdownRef.current = liveSettings.googleSyncInterval * 60;
      setCountdownDisplay(countdownRef.current);
      intervalRef.current = setInterval(() => {
        if (countdownRef.current === null) return;
        if (countdownRef.current > 0) {
          countdownRef.current -= 1;
          setCountdownDisplay(countdownRef.current);
        } else if (countdownRef.current === 0) {
          countdownRef.current = null;
          setCountdownDisplay(null);
          syncFnRef.current();
        }
      }, 1000);
    } else {
      countdownRef.current = null;
      setCountdownDisplay(null);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    liveSettings?.googleAutoSyncEnabled,
    liveSettings?.googleBackupConnected,
    liveSettings?.googleSyncInterval,
  ]);

  // If connectivity returns while auto-sync is enabled, retry immediately
  // rather than waiting for the full interval to elapse.
  useEffect(() => {
    const handleOnline = () => {
      if (
        liveSettings?.googleAutoSyncEnabled &&
        liveSettings?.googleBackupConnected &&
        liveSettings?.googleBackupUrl
      ) {
        syncFnRef.current?.();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [
    liveSettings?.googleAutoSyncEnabled,
    liveSettings?.googleBackupConnected,
    liveSettings?.googleBackupUrl,
  ]);

  const formatCountdown = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Sidebar width constants
  const mobileSidebarWidth = 260;
  const expandedDesktopSidebarWidth = 234;
  const collapsedDesktopSidebarWidth = 72;
  const desktopSidebarWidth = collapsed ? collapsedDesktopSidebarWidth : expandedDesktopSidebarWidth;
  const effectiveWidth = mobileOpen ? mobileSidebarWidth : desktopSidebarWidth;

  const handleLogout = async () => {
    try {
      await authService.logout(liveSettings);
    } catch (err) {
      showToast('Logout failed', 'error');
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 xl:hidden transition-opacity duration-200 ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onMobileClose}
      />

      {/* Sidebar container */}
      <motion.div
        animate={{ width: effectiveWidth }}
        transition={{ duration: mobileOpen ? 0 : 0.2, ease: 'easeInOut' }}
        className={`h-[100dvh] bg-slate-900 text-white flex flex-col border-r border-slate-800 shrink-0 overflow-hidden z-40 fixed top-0 left-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } xl:sticky xl:top-0 xl:relative xl:translate-x-0 transition-transform duration-200 ease-in-out xl:transition-none`}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-slate-800 min-h-[56px]">
          {!collapsed && (
            <div className="flex items-center gap-2 font-bold text-xl text-white overflow-hidden whitespace-nowrap">
              <span className="text-indigo-400">🛡️</span> ScopeShield
            </div>
          )}
          {collapsed && <span className="text-xl mx-auto text-indigo-400">🛡️</span>}
          
          {/* Desktop collapse/expand */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white transition-colors hidden xl:block"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <SafeIcon icon={collapsed ? FiIcons.FiChevronsRight : FiIcons.FiChevronsLeft} />
          </button>

          {/* Mobile close button */}
          {mobileOpen && (
            <button
              onClick={onMobileClose}
              className="text-slate-400 hover:text-white transition-colors xl:hidden"
              style={{ minWidth: '44px', minHeight: '44px' }}
              aria-label="Close navigation"
            >
              <SafeIcon icon={FiIcons.FiX} className="text-xl" />
            </button>
          )}
        </div>

        {/* Search Input Button */}
        <div className="px-3 mb-2 mt-4">
          <button
            onClick={onOpenProposalSearch}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-slate-400 hover:bg-slate-800 hover:text-white ${collapsed ? 'justify-center' : 'justify-between'}`}
            title={collapsed ? 'Search proposals (Ctrl/Command K)' : ''}
            style={{ minHeight: '44px' }}
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiSearch} className="text-xl shrink-0" />
              {!collapsed && <span className="font-medium whitespace-nowrap">Search proposals</span>}
            </div>
            {!collapsed && (
              <span className="text-[10px] font-bold bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 shrink-0">
                {navigator.platform?.toUpperCase().indexOf('MAC') >= 0 ? '⌘K' : 'Ctrl K'}
              </span>
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 py-2 flex flex-col gap-2 px-3 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => {
                if (mobileOpen) onMobileClose?.();
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                  isActive ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`
              }
              title={collapsed ? item.label : ''}
              style={{ minHeight: '44px' }}
            >
              <SafeIcon icon={item.icon} className="text-xl shrink-0" />
              {!collapsed && <span className="font-medium whitespace-nowrap">{item.label}</span>}
            </NavLink>
          ))}
        </div>

        {/* Sync Status */}
        <div className="p-4 border-t border-slate-800">
          {!collapsed && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Cloud Sync
                </span>
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      syncStatus === 'Syncing'
                        ? 'bg-amber-400 animate-pulse'
                        : syncStatus === 'Synced'
                        ? 'bg-green-500'
                        : syncStatus === 'Pending'
                        ? 'bg-amber-500'
                        : syncStatus === 'Error'
                        ? 'bg-red-500'
                        : 'bg-slate-600'
                    }`}
                  />
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                    {syncStatus}
                  </span>
                </div>
              </div>

              {isConfigured ? (
                <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700/50">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-slate-500 font-bold uppercase leading-none mb-1">
                        Next Auto-Sync
                      </span>
                      <span className="text-xs font-mono font-bold text-indigo-300">
                        {countdownDisplay !== null ? formatCountdown(countdownDisplay) : '--:--'}
                      </span>
                    </div>
                    <button
                      onClick={() => syncFnRef.current()}
                      disabled={syncStatus === 'Syncing'}
                      className="p-1.5 bg-slate-700 hover:bg-indigo-600 rounded text-slate-300 hover:text-white transition-all disabled:opacity-50"
                      style={{ minWidth: '44px', minHeight: '44px' }}
                    >
                      <SafeIcon icon={FiIcons.FiRefreshCw} className={syncStatus === 'Syncing' ? 'animate-spin' : ''} />
                    </button>
                  </div>
                  {liveSettings?.googleBackupLastSyncAt && (
                    <div className="mt-2 text-[8px] text-slate-600 font-medium uppercase tracking-tight">
                      Last sync: {new Date(liveSettings.googleBackupLastSyncAt).toLocaleString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[9px] text-slate-600 font-bold uppercase tracking-tight text-center italic py-2">
                  Not Configured
                </div>
              )}
            </div>
          )}

          {/* Logout Button */}
          {hasSession && (
            <div className={!collapsed ? 'mb-3' : ''}>
              <button
                onClick={handleLogout}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all text-slate-400 hover:text-red-400 hover:bg-red-500/10 ${
                  collapsed ? 'justify-center' : ''
                }`}
                style={{ minHeight: '44px' }}
                title="Logout"
              >
                <SafeIcon icon={FiIcons.FiLogOut} className="text-lg shrink-0" />
                {!collapsed && <span className="text-[10px] font-black uppercase tracking-widest">Logout</span>}
              </button>
            </div>
          )}

          <div className="text-slate-500 text-[10px] text-center font-bold tracking-widest">
            {!collapsed ? 'SCOPESHIELD V1.0' : 'V1.0'}
          </div>
        </div>
      </motion.div>
    </>
  );
}